import React, { useEffect, useState, useRef } from 'react';
import { AppState, Platform, useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';
import { Feather } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { useVideoPlayer } from 'expo-video';

import Button from '../../components/Button';
import Link from '../../components/Link';
import Spacer from '../../components/Spacer';
import { H2, H5, P, Subtitle } from '../../components/Typography';
import Colors from '../../constants/Colors';
import LogInModal from './modals/LogInModal';
import SignUpModal from './modals/SignUpModal';
import IconButton from '../../components/IconButton';
import { IntroParamList } from '../../navigation/IntroNavigator';
import { RouteProp, useRoute } from '@react-navigation/native';
import VideoPlayer from '../../components/VideoPlayer';

const StyledScrollView = styled.ScrollView`
  background-color: ${Colors.secondaryCreme};
`;

const StyledImageBackground = styled.Image`
  position: absolute;
  top: 0;
  right: 0;
  width: 45%;
  max-width: 385px;
  height: 100%;
  z-index: -1;
`;

const StyledContainer = styled.View<{ isDesktop: boolean }>`
  width: ${p => (p.isDesktop ? '85%' : '100%')};
  align-items: center;

  padding: 0px ${p => (p.isDesktop ? 80 : 0)}px;
`;

const StyledContentContainer = styled.View<{ isTwoColumn: boolean }>`
  flex-direction: ${p => (p.isTwoColumn ? 'row' : 'column')};
  width: 100%;
  max-width: 1000px;
  align-items: center;
`;

const StyledLeftColumnContainer = styled.View<{ isDesktop: boolean }>`
  width: 100%;
  ${p => (p.isDesktop ? 'max-width: 681px;' : '')}
`;

const StyledHeroView = styled.View<{ isDesktop: boolean }>`
  padding: ${p =>
    p.isDesktop
      ? `${Platform.OS === 'web' ? '100px' : '10%'} 0px 32px 0px`
      : `${Platform.OS === 'web' ? '100px' : '40px'} 16px 10px 16px`};
  max-width: 1080px;
`;

const StyledButton = styled(Button)`
  width: 170px;
`;

const StyledWatchAVideoContainerView = styled.View`
  align-items: center;
`;

const StyledVideoPlayer = styled(VideoPlayer)`
  width: 100%;
  height: 100%;
`;

const StyledWatchAVideoView = styled.View`
  width: 100%;
  max-width: 1440px;
  height: undefined;
  aspect-ratio: 2;
`;

const StyledSignUpLoginContainer = styled.View<{ isTwoColumn: boolean }>`
  align-items: center;
  padding: 30px 0px;
  ${p =>
    p.isTwoColumn
      ? 'align-self: flex-end; margin-left: 60px; margin-bottom: 100px;'
      : ''}
`;

const StyledSubtitle = styled(Subtitle)`
  line-height: 45px;
`;

const StyledH2 = styled(H2)`
  font-size: 40px;
  line-height: 42px;
  letter-spacing: -0.75px;
  margin-right: 20px;
`;

const StyledH5 = styled(H5)<{ isDesktop: boolean }>`
  font-size: 18px;
  line-height: 22.9px;
  padding-right: ${p => (p.isDesktop ? 0 : 40)}px;
`;

const StyledP = styled(P)`
  font-size: 18px;
  line-height: 22px;
  letter-spacing: -0.7px;
  text-align: center;
`;

const StyledLink = styled(Link)`
  color: ${Colors.primaryPurple};
  font-size: 18px;
  line-height: 27px;
  letter-spacing: -0.7px;
  text-align: center;
`;

const StyledIconButton = styled(IconButton)`
  margin: 4px 0px 12px -8px;
`;

const StyledMobiledBackgroundContainer = styled.View`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: absolute;
  z-index: -1;
  flex: 1;
`;

const StyledMobileBackgroundImage = styled.Image`
  position: absolute;
  z-index: -1;
  height: 100%;
  width: auto;
  top: ${Platform.OS === 'web' ? 40 : 0}px;
  right: 0;
  aspect-ratio: 0.405;
`;

const IntroScreen: React.FC = () => {
  const route = useRoute<RouteProp<IntroParamList, 'Intro'>>();
  const window = useWindowDimensions();
  const isDesktop = window.width >= 600;
  const isTwoColumn = window.width >= 1230;
  const [signUp, setSignUp] = useState(false);
  const [logIn, setLogIn] = useState(false);
  const videoURI =
    'https://chloe.peoplescience.health/files/videos/introducing_people_science%20(1080p)-480.mp4';
  const player = useVideoPlayer({
    uri: videoURI,
    contentType: 'progressive'
  });

  const navigation = useNavigation<StackNavigationProp<any>>();
  const [showThumbnail, setShowThumbnail] = useState(true);
  const appState = useRef(AppState.currentState);

  useEffect(() => {
    if (route.params) {
      const isLoginModalOpen = route.params.isLoginModalOpen;
      setLogIn(isLoginModalOpen ?? false);
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      return;
    }
    const subscription = AppState.addEventListener(
      'change',
      _handleAppStateChange
    );
    return () => {
      subscription.remove();
    };
  }, []);
  const _handleAppStateChange = (nextAppState: any) => {
    if (appState.current.match(/inactive|background/)) {
      player.pause();
    }
    appState.current = nextAppState;
  };

  return (
    <StyledScrollView
      scrollIndicatorInsets={{ right: 1 }}
      contentContainerStyle={{
        flexGrow: 1,
        justifyContent: 'space-between'
      }}
    >
      {isDesktop && (
        <StyledImageBackground
          resizeMode='stretch'
          source={require('../../assets/intro/intro-background.png')}
        />
      )}
      <StyledContainer isDesktop={isDesktop}>
        <StyledContentContainer isTwoColumn={isTwoColumn}>
          <StyledLeftColumnContainer isDesktop={isDesktop}>
            <StyledHeroView isDesktop={isDesktop}>
              {!isDesktop && (
                <StyledMobiledBackgroundContainer>
                  <StyledMobileBackgroundImage
                    resizeMode='contain'
                    source={require('../../assets/intro/intro-background.png')}
                  />
                </StyledMobiledBackgroundContainer>
              )}

              {Platform.OS !== 'web' && (
                <StyledIconButton
                  onPress={() => {
                    player.pause();
                    navigation.navigate('IntroAbout5Screen');
                  }}
                >
                  <Feather
                    name='arrow-left'
                    size={24}
                    color={Colors.primaryPurple}
                  />
                </StyledIconButton>
              )}
              <StyledSubtitle color='medium'>Introducing Chloe:</StyledSubtitle>
              <StyledH2 padding={2} color='medium'>
                A personal tool to help you explore alternative medicines.
              </StyledH2>
              <StyledH5 isDesktop={isDesktop} padding={3} color='medium'>
                Through Chloe, you can self-experiment and explore alternative
                medicines as well as join a community of similar health-curious
                individuals to understand these medicines more broadly.
              </StyledH5>
              <StyledH5 isDesktop={isDesktop} padding={1}>
                The revolution for better health is in beta testing—join us.
              </StyledH5>
            </StyledHeroView>
            <StyledWatchAVideoContainerView>
              <StyledWatchAVideoView>
                <StyledVideoPlayer
                  imageSource={require('../../assets/home/video-placeholder.jpeg')}
                  customPlayer={player}
                  videoSource={{
                    uri: 'https://chloe.peoplescience.health/files/videos/introducing_people_science%20(1080p)-480.mp4'
                  }}
                />
              </StyledWatchAVideoView>
            </StyledWatchAVideoContainerView>
          </StyledLeftColumnContainer>
          <StyledSignUpLoginContainer isTwoColumn={isTwoColumn}>
            <StyledButton
              outlined
              onPress={() => {
                player.pause();
                setSignUp(true);
              }}
            >
              Sign Up
            </StyledButton>
            <Spacer size={3} />
            <StyledP color='medium'>Already have an account?</StyledP>
            <StyledLink
              onPress={() => {
                player.pause();
                setLogIn(true);
              }}
            >
              Log in
            </StyledLink>
          </StyledSignUpLoginContainer>
        </StyledContentContainer>
      </StyledContainer>
      {signUp && (
        <SignUpModal
          visible
          onRequestClose={() => setSignUp(false)}
          onRequestLogin={() => {
            setSignUp(false);
            setLogIn(true);
          }}
        />
      )}
      {logIn && (
        <LogInModal
          visible
          onRequestClose={() => setLogIn(false)}
          onRequestSignUp={() => {
            setLogIn(false);
            setSignUp(true);
          }}
        />
      )}
    </StyledScrollView>
  );
};

export default IntroScreen;
